import React, { useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import styled from 'styled-components';
import _ from 'lodash';

import { Button, Card, Flex, Grid, Image, Spacer, token } from '@zillow/constellation';
import PropTypes from 'prop-types';

import IncomeDocumentInfo from './IncomeDocumentInfo';
import SingleApplicantDocumentViewer from './SingleApplicantDocumentViewer';
import useQueryVariables from '../../../hooks/useQueryVariables';
import { getPreviewUrl } from './IncomeDocumentUtils';
import { logError } from '@zg-rentals/log-error';

const StyledContainer = styled(Spacer)`
  width: 100%;
`;

const ThumbnailContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  height: 8rem;
  max-width: 8rem;
  min-width: 6rem;
  overflow: hidden;
`;

const StyledImage = styled(Image)`
  // Needed by IE11.
  width: 100%;
`;

const DocumentCard = styled(Card)`
  background-color: ${token('colors.backgroundLight')};
`;

export const SINGLE_APPLICANT_QUERY = gql`
  query ViewSharedDocument(
    $propertyId: String!
    $applicationId: String!
    $applicantId: String!
    $zgAuthZone: ZgAuthZone!
    $documentId: String!
    $currentIncomeId: String!
  ) {
    property(id: $propertyId) {
      id
      application(id: $applicationId) {
        id
        applicant(id: $applicantId, zgAuthZone: $zgAuthZone) {
          id
          currentIncome(id: $currentIncomeId) {
            id
            document(id: $documentId) {
              id
              viewerUrl
            }
          }
        }
      }
    }
  }
`;

export default function DocumentController({
  applicant,
  documents,
  incomeSourceId,
  onOpenFileViewer,
  renderDocumentViewer,
}) {
  const [isFileViewerOpen, setIsFileViewerOpen] = useState(false);
  const [selectedIncomeDocumentId, setSelectedIncomeDocumentId] = useState(documents[0].id);
  const { applicationId, applicantId, zgAuthZone, propertyId } = useQueryVariables();

  const onError = (error) => {
    if (error) {
      logError({ error });
    }
  };

  const [fetchDocuments, { loading, called, data }] = useLazyQuery(SINGLE_APPLICANT_QUERY, { onError });

  function openFileViewer(documentId) {
    if (!renderDocumentViewer) {
      fetchDocuments({
        variables: { documentId, applicantId, zgAuthZone, applicationId, propertyId, currentIncomeId: incomeSourceId },
      });
    }
    setSelectedIncomeDocumentId(documentId);
    setIsFileViewerOpen(true);

    if (onOpenFileViewer && typeof onOpenFileViewer === 'function') {
      onOpenFileViewer();
    }
  }

  function closeFileViewer() {
    setIsFileViewerOpen(false);
  }

  return (
    <StyledContainer id="document-controller" paddingTop="sm" className="dynamic-section__documentController">
      {renderDocumentViewer
        ? renderDocumentViewer({
            isOpen: isFileViewerOpen,
            handleOnClose: closeFileViewer,
            selectedIncomeDocumentId,
            setSelectedIncomeDocumentId,
          })
        : called &&
          !loading && (
            <SingleApplicantDocumentViewer
              documentEditorUrl={_.get(data, 'property.application.applicant.currentIncome.document.viewerUrl')}
              isOpen={isFileViewerOpen}
              handleOnClose={closeFileViewer}
            />
          )}

      <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="sm" data-test="document-controller-container">
        {documents.map((incomeDocument) => {
          return (
            Boolean(incomeDocument.fileName) && (
              <Grid
                gridColumnEnd={
                  !renderDocumentViewer
                    ? { default: 'span 12', md: 'span 6', xl: 'span 4' }
                    : { default: 'span 12', lg: 'span 6' }
                }
                key={incomeDocument.id}
              >
                <DocumentCard
                  cardType="secondary"
                  aria-label={`open ${incomeDocument.fileName}`}
                  data-test="document-controller-document-card"
                  id={`document-controller-document-card-${incomeDocument.id}`}
                >
                  <Flex>
                    <Flex display="flex" alignItems="center">
                      <ThumbnailContainer className="thumbnail__documentController">
                        <StyledImage
                          alt={`${incomeDocument.fileName} preview`}
                          src={getPreviewUrl(incomeDocument.previewUrl)}
                        />
                      </ThumbnailContainer>
                      <IncomeDocumentInfo
                        {...{
                          applicant: applicant,
                          fileName: incomeDocument.fileName,
                          expirationFloat: incomeDocument.expirationDateUtc ?? incomeDocument.expirationDateTime,
                        }}
                        className="income-document-info__documentController"
                      />
                    </Flex>
                    <Button
                      className="review-button_documentController"
                      data-test="document-controller-document-button"
                      id={`document-controller-document-button-${incomeDocument.id}`}
                      buttonType="secondary"
                      onClick={() => openFileViewer(incomeDocument.id)}
                      fluid
                    >
                      Review document
                    </Button>
                  </Flex>
                </DocumentCard>
              </Grid>
            )
          );
        })}
      </Grid>
    </StyledContainer>
  );
}

DocumentController.propTypes = {
  incomeSourceId: PropTypes.string.isRequired,
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      fileName: PropTypes.string.isRequired,
      previewUrl: PropTypes.string.isRequired,
      expirationDateUtc: PropTypes.number.isRequired,
      viewerUrl: PropTypes.string,
    }),
  ),
  renderDocumentViewer: PropTypes.func,
  onOpenFileViewer: PropTypes.func,
};

DocumentController.defaultProps = {
  renderDocumentViewer: null,
  onOpenFileViewer: null,
};
